import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Alert } from "../../../../ui/components/Alert";
import { Loader } from "../../../../ui/components/Loader";
import { Tooltip } from "../../../../ui/components/Tooltip";
import * as SubframeCore from "@subframe/core";
import { FeatherHelpCircle, FeatherRefreshCw } from "@subframe/core";
import { useAgentReviewDashboard } from "../hooks/useAgentReviewDashboard";

/**
 * ZOUR-67 — is the agent worth reading?
 *
 * Four questions, in the order someone actually asks them: how much of the
 * queue does it clear, what is it finding, how often is it wrong, and what does
 * it cost. Every number counts each bill's latest run only.
 *
 * The false-alarm rate is the one to watch, and it is deliberately computed
 * over findings someone has ruled on rather than over all of them. Counting
 * undecided findings as correct would make the rate drift down as the queue
 * grows, which would make the agent look better the less anyone read it.
 */

const WINDOWS = [7, 30, 90];

const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  help?: string;
  tone?: "default" | "good" | "warn";
}> = ({ label, value, help, tone = "default" }) => (
  <div className="flex min-w-[180px] flex-1 flex-col gap-1 rounded-md border border-solid border-neutral-border bg-default-background p-4">
    <div className="flex items-center gap-1">
      <span className="text-caption font-caption text-subtext-color">{label}</span>
      {help && (
        <SubframeCore.Tooltip.Provider>
          <SubframeCore.Tooltip.Root>
            <SubframeCore.Tooltip.Trigger asChild={true}>
              <span className="inline-flex">
                <FeatherHelpCircle className="text-caption font-caption text-subtext-color" />
              </span>
            </SubframeCore.Tooltip.Trigger>
            <SubframeCore.Tooltip.Portal>
              <SubframeCore.Tooltip.Content asChild={true} sideOffset={4}>
                <Tooltip>{help}</Tooltip>
              </SubframeCore.Tooltip.Content>
            </SubframeCore.Tooltip.Portal>
          </SubframeCore.Tooltip.Root>
        </SubframeCore.Tooltip.Provider>
      )}
    </div>
    <span
      className={`text-heading-2 font-heading-2 ${
        tone === "good"
          ? "text-success-700"
          : tone === "warn"
            ? "text-warning-700"
            : "text-default-font"
      }`}
    >
      {value}
    </span>
  </div>
);

const percent = (value: number | null): string =>
  value === null ? "—" : `${(value * 100).toFixed(0)}%`;

export const AgentReviewDashboard: React.FC = () => {
  const { data, loading, error, days, setDays, includePosted, setIncludePosted, reload } =
    useAgentReviewDashboard();

  if (loading && !data) {
    return (
      <div className="flex w-full items-center gap-2 py-8">
        <Loader />
        <span className="text-body font-body text-subtext-color">
          Loading the numbers…
        </span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="error" title="Could not load" description={error} />;
  }

  if (!data) return null;

  const { dispositions } = data;
  const decided = dispositions.not_an_error + dispositions.confirmed;

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Window */}
      <div className="flex w-full flex-wrap items-center gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setDays(w)}
            className={`rounded-md border border-solid px-3 py-1.5 ${
              days === w
                ? "border-brand-600 bg-brand-50"
                : "border-neutral-border bg-default-background"
            }`}
          >
            <span className="text-body font-body text-default-font">
              Last {w} days
            </span>
          </button>
        ))}
        {/* The same switch as the Queue, and it moves every figure on this tab
            at once — headline, banner, both tables, the false-alarm rate and
            cost — because they all read one subquery in the API. */}
        <label className="ml-auto flex cursor-pointer items-center gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-1.5">
          <input
            type="checkbox"
            checked={includePosted}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setIncludePosted(e.target.checked)
            }
          />
          <span className="whitespace-nowrap text-body font-body text-default-font">
            Include posted bills
          </span>
        </label>

        <Button
          variant="neutral-secondary"
          icon={<FeatherRefreshCw />}
          loading={loading}
          onClick={reload}
        >
          Refresh
        </Button>
      </div>

      {data.include_posted && data.posted_bills_included > 0 && (
        <span className="text-body font-body text-subtext-color">
          Includes {data.posted_bills_included} posted bill
          {data.posted_bills_included === 1 ? "" : "s"} reviewed as they arrived.
        </span>
      )}

      {/* Not a metric but a diagnosis: if most of what needs attention needs it
          only because nothing could be read, the problem is the bucket, not the
          bills. */}
      {data.needs_attention_no_document > 0 && (
        <Alert
          variant="warning"
          title={`${data.needs_attention_no_document} bill(s) were reviewed without their document`}
          description="Their PDFs could not be fetched, so none of them can be Ready and none were checked against an invoice. If this is most of the queue, the tenant's S3 access is the thing to fix."
        />
      )}

      {data.bills_checked === 0 && (
        <Alert
          variant="neutral"
          title="Nothing reviewed in this window"
          description="Either the background check has not run yet, or no bills were due. Widen the window, or check that the cron is registered."
        />
      )}

      {/* How much of the queue it clears */}
      <div className="flex w-full flex-wrap gap-3">
        <Metric
          label="Bills checked"
          value={data.bills_checked}
          help="Counting each bill's latest run only, so a re-checked bill is still one bill."
        />
        <Metric
          label="Ready to post"
          value={data.ready}
          tone="good"
          help="Vendor set, every blocking validation key valid, and no document mismatch found."
        />
        <Metric
          label="Needs attention"
          value={
            <span className="flex items-baseline gap-2">
              {data.needs_attention}
              {data.needs_attention_no_document > 0 && (
                <span className="text-caption font-caption text-subtext-color">
                  {data.needs_attention_no_document} with no document
                </span>
              )}
            </span>
          }
          tone="warn"
          help="A blocking check failed, the document could not be read, or the agent found something the document contradicts."
        />
        <Metric
          label="Checks that errored"
          value={data.errored}
          help="The review itself failed — a model error, a bad snapshot. The reason is on the run."
        />
      </div>

      {/* What it is finding, and whether anyone believes it */}
      <div className="flex w-full flex-wrap gap-3">
        <Metric
          label="Bills with findings"
          value={
            data.share_with_findings === null
              ? data.bills_with_findings
              : `${data.bills_with_findings} (${percent(data.share_with_findings)})`
          }
          help="How much of the queue the agent has something to say about."
        />
        {/* The denominator is on the face of the tile, not just in the tooltip.
            A bare "100%" over one decided finding reads as "the agent is always
            wrong"; "100% (1 of 1)" reads as what it is — too early to tell. */}
        <Metric
          label="False-alarm rate"
          value={
            <span className="flex items-baseline gap-2">
              {percent(dispositions.false_alarm_rate)}
              {decided > 0 && (
                <span className="text-caption font-caption text-subtext-color">
                  {dispositions.not_an_error} of {decided} decided
                </span>
              )}
            </span>
          }
          tone={
            // Only worth colouring once there is enough decided to mean
            // something. Five is not a statistical threshold, it is the point
            // below which a colour would be asserting more than the data does.
            decided >= 5 &&
            dispositions.false_alarm_rate !== null &&
            dispositions.false_alarm_rate > 0.3
              ? "warn"
              : "default"
          }
          help={
            decided === 0
              ? "Nobody has marked a finding yet, so there is nothing to compute this from."
              : `Of the ${decided} findings somebody has ruled on, the share marked "Not an error". Undecided findings are left out — counting them as correct would make this drift down the less anyone reads.`
          }
        />
        <Metric
          label="Cost per bill"
          value={`$${data.cost_per_bill.toFixed(4)}`}
          help={`$${data.cost_usd.toFixed(2)} over this window.`}
        />
        <Metric
          label="Findings awaiting a verdict"
          value={dispositions.undecided}
          help='Findings nobody has marked "Not an error" or confirmed.'
        />
      </div>

      {/* By type */}
      <div className="flex w-full flex-col gap-2">
        <span className="text-heading-3 font-heading-3 text-default-font">
          What it is flagging
        </span>
        {data.findings_by_field.length === 0 ? (
          <span className="text-body font-body text-subtext-color">
            No findings in this window.
          </span>
        ) : (
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Field</Table.HeaderCell>
                <Table.HeaderCell>Group</Table.HeaderCell>
                <Table.HeaderCell>Findings</Table.HeaderCell>
                <Table.HeaderCell>Called not an error</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {data.findings_by_field.map((row, i) => {
              const n = Number(row.n) || 0;
              const wrong = Number(row.not_an_error) || 0;
              return (
                <Table.Row key={`${row.field}-${row.finding_group}-${i}`}>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font break-words">
                      {row.field}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      variant={
                        row.finding_group === "document_mismatch"
                          ? "error"
                          : "neutral"
                      }
                    >
                      {row.finding_group === "document_mismatch"
                        ? "Mismatch"
                        : "Advisory"}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">{n}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className={`text-body font-body ${
                        wrong > 0 ? "text-warning-700" : "text-subtext-color"
                      }`}
                    >
                      {wrong > 0 ? `${wrong} of ${n}` : "—"}
                    </span>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table>
        )}
      </div>

      {/* By vendor */}
      <div className="flex w-full flex-col gap-2">
        <span className="text-heading-3 font-heading-3 text-default-font">
          Which vendors cost the most attention
        </span>
        {data.findings_by_vendor.length === 0 ? (
          <span className="text-body font-body text-subtext-color">
            No findings in this window.
          </span>
        ) : (
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Vendor</Table.HeaderCell>
                <Table.HeaderCell>Bills with findings</Table.HeaderCell>
                <Table.HeaderCell>Findings</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {data.findings_by_vendor.map((row, i) => (
              <Table.Row key={`${row.vendor_id ?? "none"}-${i}`}>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {row.vendor_name || row.vendor_id || "Unknown vendor"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {row.bills}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {row.findings}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>
        )}
      </div>

      {/* The Queue calls a bill with a changed hash "Checking"; this tab still
          counts its last run. Both are right — one asks what needs doing now,
          the other what the agent has done — but two tabs of one page saying
          different things about the same bill needs saying out loud. */}
      <span className="text-caption font-caption text-subtext-color">
        Every figure counts each bill's latest run only, over the last {days} days
        — including runs on bills that have changed since, which the Queue shows
        as Checking.
        {data.include_posted
          ? " Posted bills are included, scored as they arrived rather than as they stand now."
          : " Bills already posted to Zoho are left out."}
      </span>
    </div>
  );
};

export default AgentReviewDashboard;
